import { createContext, useState, useContext, useCallback, useMemo } from 'react';
import { SessionService } from '@nextgen-web-framework/core';
import { UNAVAILABLE_CONTEXT_ERROR } from '../../ErrorLabels';
import { SessionContextValue, SessionProviderProps, Session, UserType, Actor, AccountType } from './types/propsType';

const SessionContext = createContext<SessionContextValue>({
  session: {},
  updateSession: () => {
    /* do nothing */
  },
});

export function SessionProvider(props: SessionProviderProps) {
  const [session, setSession] = useState(props.session);
  if (props.session) {
    SessionService.setSession(props.session);
  }
  const updateSession = useCallback((session: Session) => {
    setSession((prevSession) => {
      const updatedSession: Session = {
        ...prevSession,
        ...session,
      };
      SessionService.setSession(updatedSession);
      return updatedSession;
    });
  }, []);
  const value = useMemo(
    () => ({
      session: session as Session,
      updateSession: updateSession,
    }),
    [session, updateSession],
  );
  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

export function useSession() {
  const { session }: SessionContextValue = useContext(SessionContext);
  // TODO: This logic will change based on user type flow changes from platform post alpha 5
  const isAnonymous = () => !session.type || session.type === UserType.ANONYMOUS;
  const isRegistered = () => session.type === UserType.REGISTERED;
  const isABO = () => session.type === UserType.ABO;
  const isMember = () => session.type === UserType.MEMBER;
  const isEmployee = () => session.type === UserType.EMPLOYEE;
  const isActor = () => !!session.act;
  return { session, isAnonymous, isRegistered, isABO, isMember, isEmployee, isActor };
}

export function useSessionUpdate() {
  if (!SessionContext) {
    throw new Error(UNAVAILABLE_CONTEXT_ERROR);
  }
  const { updateSession }: SessionContextValue = useContext(SessionContext);

  return updateSession;
}
export type { Session, Actor };
export { UserType, AccountType };
